import React from "react";
import * as Sentry from "@sentry/react";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

export default class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Reporta ao Sentry com contexto adicional
    Sentry.captureException(error, (scope) => {
      scope.setExtras({
        componentStack: errorInfo?.componentStack,
        url: typeof window !== 'undefined' ? window.location?.href : undefined,
        pathname: typeof window !== 'undefined' ? window.location?.pathname : undefined,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      });
      scope.setLevel('error');
      return scope;
    });

    // Log local para desenvolvimento
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, errorInfo);
  }

  handleRetry = () => {
    // Resetar o estado para tentar re-renderizar
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-gray-50 dark:bg-gray-950">
          <div className="max-w-lg w-full rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-900 p-6 text-center">
            <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-red-600 dark:bg-red-900/30">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-alert-triangle"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" x2="12" y1="9" y2="13"></line><line x1="12" x2="12.01" y1="17" y2="17"></line></svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-1">Ocorreu um erro inesperado</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Tente novamente. Se o problema persistir, recarregue a página.</p>
            <div className="flex items-center justify-center gap-3">
              <button onClick={this.handleRetry} className="px-4 py-2 rounded-md bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-white text-sm">Tentar novamente</button>
              <button onClick={() => window.location.reload()} className="px-4 py-2 rounded-md bg-brand-600 hover:bg-brand-700 text-white text-sm">Recarregar</button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
